/**
 * Category groupings for the /programs/:state catalogue page (Phase 4 rewrite).
 *
 * This file is presentation-only metadata. It maps course IDs from
 * `src/data/courses/courses.ts` into the ordered category groups used by
 * Programs.tsx. It does NOT define new courses, does NOT change pricing slugs,
 * and is not consumed by anything outside the Programs page.
 *
 * Category render order (spec Section 5.1):
 *   1. Foundations
 *   2. Selective and Scholarship
 *   3. NAPLAN
 *   4. Enrichment
 *   5. After-school
 */

import { isCourseFamilyAvailableIn } from '@/config/courseStateMapping';

export type ProgramCategoryId =
  | 'foundations'
  | 'selective-scholarship'
  | 'naplan'
  | 'enrichment'
  | 'afterschool';

export type ProgramCategory = {
  id: ProgramCategoryId;
  title: string;
  blurb: string;
  /** Course IDs (from COURSES in src/data/courses/courses.ts). */
  courseIds: string[];
};

export const PROGRAM_CATEGORIES: ProgramCategory[] = [
  {
    id: 'foundations',
    title: 'Foundations',
    blurb: 'Core literacy and numeracy across Kindergarten, Prep or Pre-primary to Year 8.',
    courseIds: ['foundations'],
  },
  {
    id: 'selective-scholarship',
    title: 'Selective and Scholarship',
    blurb:
      'Targeted preparation for Opportunity Class, Selective High School and private scholarship entry exams.',
    courseIds: [
      // NSW
      'oc-tmrw',
      'selective-tmrw',
      'tmrw-leap',
      // VIC
      'scholarship-exam',
      'vsec',
      // WA
      'aset-gate',
    ],
  },
  {
    id: 'naplan',
    title: 'NAPLAN',
    blurb: 'Short-cycle preparation aligned to the NAPLAN test window for Years 3, 5 and 7.',
    courseIds: ['naplan-nsw', 'naplan-vic'],
  },
  {
    id: 'afterschool',
    title: 'After-school',
    blurb: 'Whole-of-afternoon support combining homework help, skill development and supervised activities.',
    courseIds: ['afterschool-vic'],
  },
];

/**
 * State-aware category blurbs. Derived from COURSE_FAMILY_STATES so a state
 * never reads copy about an exam it cannot sit.
 */
export const categoryBlurbForState = (
  category: ProgramCategory,
  state: string,
): string => {
  if (category.id === 'selective-scholarship') {
    const hasNswSelective = isCourseFamilyAvailableIn('selective_nsw', state);
    const hasVicSelective = isCourseFamilyAvailableIn('selective_vic', state);
    const hasGate = isCourseFamilyAvailableIn('gate', state);

    if (!hasNswSelective && !hasVicSelective && hasGate) {
      return "Preparation for GATE, Western Australia's academic selection test.";
    }
  }

  return category.blurb;
};


// -----------------------------------------------------------------------------
// Year-range model used by both the year chip grid and the top-of-page filter.
// -----------------------------------------------------------------------------

/** 9-column chip grid: Prep, Year 1, Year 2, ..., Year 8. */
export const YEAR_CHIPS: { value: number; label: string }[] = [
  { value: 0, label: 'Prep' },
  { value: 1, label: 'Y1' },
  { value: 2, label: 'Y2' },
  { value: 3, label: 'Y3' },
  { value: 4, label: 'Y4' },
  { value: 5, label: 'Y5' },
  { value: 6, label: 'Y6' },
  { value: 7, label: 'Y7' },
  { value: 8, label: 'Y8' },
];

export type YearFilterId = 'all' | 'prep-y2' | 'y3-y6' | 'y7-y8';

export const YEAR_FILTERS: { id: YearFilterId; label: string; years: number[] }[] = [
  { id: 'all', label: 'All years', years: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
  { id: 'prep-y2', label: 'Prep-Y2', years: [0, 1, 2] },
  { id: 'y3-y6', label: 'Y3-Y6', years: [3, 4, 5, 6] },
  { id: 'y7-y8', label: 'Y7-Y8', years: [7, 8] },
];

/**
 * Parse a course's free-text `yearRange` into a discrete set of year integers
 * in the 0-8 chip space. "Prep" and "Kindergarten" both map to 0.
 *
 * Handles the shapes currently used in src/data/courses/courses.ts:
 *   - "Prep / Kindergarten - Year 8"
 *   - "Prep - Year 6"
 *   - "Kindergarten - Year 8"
 *   - "Year 3 - Year 8"
 *   - "Year 5 - Year 6"
 *   - "Year 3,4 OC"                  → {3, 4}
 *   - "Year 4 - Year 8"
 *   - "Year 3, Year 5, Year 7"       → {3, 5, 7}  (discrete list, not a range)
 *   - "Year 8 Entry (Year 7 Students)" → {7}      (the cohort sitting the test)
 */
export const parseYearRange = (yearRange: string): Set<number> => {
  const text = yearRange.toLowerCase();
  const tokens = new Set<number>();

  // Prep / Kindergarten → 0
  if (/\bprep\b/.test(text) || /\bkindergarten\b/.test(text)) tokens.add(0);

  // Comma-separated explicit years (e.g. "Year 3, Year 5, Year 7", "Year 3,4 OC")
  // Detect when the string has a comma but no en/em dash (no range form).
  const hasDash = /[--]/.test(text);
  const hasComma = /,/.test(text);

  if (hasComma && !hasDash) {
    const nums = text.match(/\d+/g);
    if (nums) {
      for (const n of nums) tokens.add(Number(n));
    }
    return tokens;
  }

  // "Year 8 Entry (Year 7 Students)" - cohort is Year 7.
  if (/year\s*8\s*entry/.test(text)) {
    tokens.add(7);
    return tokens;
  }

  // Range form: take min..max of the explicit "Year N" numbers, combined with
  // Prep/Kindergarten contributions already added above.
  const yearMatches = Array.from(text.matchAll(/year\s*(\d+)/g)).map((m) => Number(m[1]));
  if (yearMatches.length > 0) {
    const min = Math.min(...yearMatches);
    const max = Math.max(...yearMatches);
    // If Prep/Kindergarten present, the lower bound is 0; otherwise it's `min`.
    const lower = tokens.has(0) ? 0 : min;
    for (let y = lower; y <= max; y++) tokens.add(y);
  }

  return tokens;
};

/** True if any year in the course's set falls inside the filter's years. */
export const courseMatchesFilter = (
  courseYears: Set<number>,
  filterYears: number[],
): boolean => filterYears.some((y) => courseYears.has(y));
